import { Box, MediaObject } from '@zillow/constellation-10';
import {
  ChatAvatar,
  type ChatBubbleVariant,
  ChatBubbleWithLinkFeatureToggle,
  getScreenReaderSenderName,
  LowPrioritySystemMessage,
  MESSAGE_STATUS,
  MessageFooter,
  type MessageStatusVariant,
} from '@zg-rentals/satellite-components';
import { useContext } from 'react';
import stringUtils from '../../../utils/stringUtils';
import type { AttachmentsForMessage } from '../../MessageAttachments';
import MessageAttachments from '../../MessageAttachments';
import { AppContext } from '../../../contexts/AppContextProvider';
import { useUserWarnings } from '../../../hooks/useUserWarnings';
import {
  emitExternalLinkClickedInExternalLinkWarningModalEvent,
  emitOpenExternalLinkWarningModalEvent,
} from '../../../constants/clickstreamEvents/externalLinkWarningModal';

type Props = {
  attachments: AttachmentsForMessage;
  conversationId: string;
  handleResendMessage(): void;
  isMessageOwner: boolean;
  messageContent: string;
  messageDateMs: string;
  messageId: string;
  senderName: string;
  sendStatus: MessageStatusVariant;
  shouldDisplaySentStatus: boolean;
};

const ListingMessageItem = ({
  attachments,
  conversationId,
  handleResendMessage,
  isMessageOwner,
  messageContent,
  messageDateMs,
  messageId,
  senderName,
  sendStatus,
  shouldDisplaySentStatus,
}: Props): JSX.Element => {
  const { brand } = useContext(AppContext);
  const hasAttachments = attachments && !!attachments.length;
  const allAttachmentsExpired = hasAttachments && attachments.every((a) => a.isAttachmentExpired);
  const allAttachmentsDeleted = hasAttachments && attachments.every((a) => a.isAttachmentDeleted);
  const isMessageSent = sendStatus === MESSAGE_STATUS.SENT;
  const isMessageContentEmpty = stringUtils.isEmptyString(messageContent);
  const chatBubbleVariant: ChatBubbleVariant = isMessageOwner ? 'messageOwner' : 'default';
  const shouldAdjustOpacity = sendStatus === MESSAGE_STATUS.SENDING || sendStatus === MESSAGE_STATUS.FAILED;

  const { externalLinkWarningData, attachmentSafetySystemMessageData } = useUserWarnings();

  return (
    <>
      <MediaObject
        media={!isMessageOwner && <ChatAvatar senderFullName={senderName} type="user" />}
        css={{
          alignItems: 'end',
          display: 'flex',
          flexDirection: isMessageOwner ? 'row-reverse' : 'row',
          gap: 0,
          whiteSpace: 'pre-wrap',
          width: '100%',
        }}
      >
        <Box css={{ opacity: shouldAdjustOpacity ? 0.4 : 1 }}>
          {!isMessageContentEmpty && (
            <ChatBubbleWithLinkFeatureToggle
              variant={chatBubbleVariant}
              enableClickableLinks
              externalLinkWarningData={externalLinkWarningData}
              brand={brand}
              senderName={senderName}
              shouldIncludeGutter
              onExternalLinkWarningModalOpen={emitOpenExternalLinkWarningModalEvent}
              onExternalLinkClickInModal={emitExternalLinkClickedInExternalLinkWarningModalEvent}
              enableHistoryIntegration={true}
            >
              {messageContent}
            </ChatBubbleWithLinkFeatureToggle>
          )}

          {hasAttachments && (
            <MessageAttachments
              attachments={attachments}
              conversationId={conversationId}
              isMessageOwner={isMessageOwner}
              isMessageSent={isMessageSent}
              messageId={messageId}
            />
          )}
        </Box>

        <MessageFooter
          isMessageOwner={isMessageOwner}
          isSenderAnnouncedByBubble={
            !isMessageContentEmpty && !!getScreenReaderSenderName(chatBubbleVariant, senderName)
          }
          messageDateMs={messageDateMs}
          resendMessage={handleResendMessage}
          senderName={senderName}
          sendStatus={sendStatus}
          shouldDisplaySentStatus={shouldDisplaySentStatus}
        />
      </MediaObject>

      {hasAttachments &&
        !isMessageOwner &&
        !allAttachmentsExpired &&
        !allAttachmentsDeleted &&
        attachmentSafetySystemMessageData && (
          <LowPrioritySystemMessage
            message={attachmentSafetySystemMessageData.disclaimerText}
            ctaText={attachmentSafetySystemMessageData.linkText}
            url={attachmentSafetySystemMessageData.linkUrl}
          />
        )}
    </>
  );
};

export default ListingMessageItem;
